import { Button, Card, CardBody, DrawerBody, DrawerFooter, IconButton, Stack, Text } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useError, useSuccess } from '@pelicargo/ui';
import { useForm } from 'react-hook-form';
import { HiEnvelope, HiPause, HiPlay } from 'react-icons/hi2';
import { z } from 'zod';

import { trpc } from '../../../config/trpc';
import { useTrackingInfo } from '../../../context/TrackingInfo';
import { ContactInput } from '../../Autocomplete/ContactInput';
import { SimpleForm } from '../../SimpleForm';

const schema = z.object({
  contact_id: z.coerce.number(),
});

type FormValues = z.infer<typeof schema>;

export const SubscribersTab = () => {
  const { subscribers, refetchSubscribers, request } = useTrackingInfo();

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
  });
  const { handleSuccess } = useSuccess();
  const { handleError } = useError();

  const { mutateAsync: toggleSubscriber } = trpc.toggleShipmentSubscriber.useMutation();
  const { mutateAsync: createShipmentSubscriber, isLoading: isCreating } = trpc.createShipmentSubscriber.useMutation();

  const { mutateAsync: sendEmail, isLoading: isSending } = trpc.sendShipmentSubscriberUpdateEmail.useMutation();

  const handleManualEmail = async (id: number) => {
    try {
      await sendEmail({
        request_id: request.id,
        contact_id: id,
      });
      handleSuccess('Email sent.');
    } catch (error) {
      handleError(error);
    }
  };

  const onValid = async (values: FormValues) => {
    try {
      if (!values) throw new Error('No values provided');
      await createShipmentSubscriber({
        request_id: request.id,
        contact_id: values.contact_id,
      });
      await refetchSubscribers();
      handleSuccess('Subscriber created.');
    } catch (error) {
      handleError(error);
    }
  };

  const handleSubmit = methods.handleSubmit(onValid);

  const renderSubscriber = (subscriber: (typeof subscribers)['subscribed'][0], unsubscribed: boolean) => {
    const onToggle = async () => {
      await toggleSubscriber({
        contact_id: subscriber.id,
        request_id: request.id,
      });
      await refetchSubscribers();
    };
    return (
      <Card key={subscriber?.id}>
        <CardBody>
          <Stack direction="row" align="center" justify="space-between" w="full">
            <Text textStyle="label" textDecoration={unsubscribed ? 'line-through' : ''}>
              {subscriber?.preferred_name}
            </Text>
            <IconButton
              aria-label="Unsubscribe subscriber"
              icon={unsubscribed ? <HiPlay /> : <HiPause />}
              variant="ghost"
              size="xs"
              colorScheme={unsubscribed ? 'green' : 'red'}
              onClick={onToggle}
            />
          </Stack>
          <Stack direction="row" align="center" justify="space-between" w="full">
            <Text textStyle="bodySmall" textDecoration={unsubscribed ? 'line-through' : ''}>
              {subscriber?.email}
            </Text>
            <IconButton
              aria-label="Manually send email"
              title="Manually send email"
              variant="ghost"
              size="xs"
              icon={<HiEnvelope />}
              isLoading={isSending}
              onClick={() => handleManualEmail(subscriber?.id)}
            />
          </Stack>
        </CardBody>
      </Card>
    );
  };

  return (
    <>
      <DrawerBody>
        <Stack>
          {subscribers?.subscribed?.map((subscriber) => renderSubscriber(subscriber, false))}
          {subscribers?.unsubscribed?.map((subscriber) => renderSubscriber(subscriber, true))}
        </Stack>
      </DrawerBody>
      <DrawerFooter borderTopWidth="1px">
        <SimpleForm {...methods} submitOnBlur={false}>
          <Stack as="form" direction="row" spacing={4} w="full" alignItems="end" onSubmit={handleSubmit}>
            <ContactInput label="Contact" name="contact_id" flexGrow="1" w="full" />
            <Button type="submit" colorScheme="brand" minW="20" isLoading={isCreating}>
              Add
            </Button>
          </Stack>
        </SimpleForm>
      </DrawerFooter>
    </>
  );
};
